/**
 * Service pour communiquer avec l'API WaterLink Connect
 * Documentation: WaterLink Connect v2 API Version 1.7 OCTOBER 2022
 */

import * as devSimulation from './devSimulation';

class WaterLinkService {
  constructor() {
    // Port par défaut de WaterLink Connect
    this.baseURL = 'http://127.0.0.1:61547/wlc';
    this.port = 61547;
  }

  /**
   * Vérifier si le service WaterLink Connect est disponible
   */
  async checkService() {
    if (devSimulation.estActive()) {
      return devSimulation.checkServiceSimule();
    }
    try {
      const response = await fetch(`${this.baseURL}/about`);
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Service non disponible');
    } catch (error) {
      console.error('Erreur de connexion au service WaterLink:', error);
      throw new Error('Impossible de se connecter au service WaterLink Connect');
    }
  }

  /**
   * Obtenir le statut de l'appareil connecté
   */
  async getStatus() {
    if (devSimulation.estActive()) {
      return devSimulation.getStatusSimule();
    }
    try {
      const response = await fetch(`${this.baseURL}/status`);
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Impossible d\'obtenir le statut de l\'appareil');
    } catch (error) {
      console.error('Erreur lors de la récupération du statut:', error);
      throw error;
    }
  }

  /**
   * Obtenir les options de test disponibles
   * @param {string} version - Version du test (pool, spa, drinkingwater, coolingwater)
   */
  async getOptions(version = null) {
    if (devSimulation.estActive()) {
      return devSimulation.getOptionsSimule();
    }
    try {
      const url = version ? `${this.baseURL}/options?version=${version}` : `${this.baseURL}/options`;
      const response = await fetch(url);
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Impossible d\'obtenir les options de test');
    } catch (error) {
      console.error('Erreur lors de la récupération des options:', error);
      throw error;
    }
  }

  /**
   * Lancer un test d'eau
   * @param {number} reagent - Type de réactif
   * @param {number} sample - Type d'échantillon
   */
  async runWaterTest(reagent, sample) {
    if (devSimulation.estActive()) {
      return devSimulation.runWaterTestSimule(reagent, sample);
    }
    try {
      const response = await fetch(`${this.baseURL}/watertest?reagent=${reagent}&sample=${sample}`);
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Impossible de lancer le test d\'eau');
    } catch (error) {
      console.error('Erreur lors du test d\'eau:', error);
      throw error;
    }
  }

  /**
   * Obtenir les logs de l'appareil
   */
  async getLogs() {
    try {
      const response = await fetch(`${this.baseURL}/log`);
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Impossible d\'obtenir les logs');
    } catch (error) {
      console.error('Erreur lors de la récupération des logs:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour le firmware de l'appareil
   */
  async updateFirmware() {
    try {
      const response = await fetch(`${this.baseURL}/update`, {
        method: 'POST'
      });
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Impossible de mettre à jour le firmware');
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      throw error;
    }
  }

  /**
   * Calibrer l'appareil
   */
  async calibrate() {
    try {
      const response = await fetch(`${this.baseURL}/calibrate`, {
        method: 'POST'
      });
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Impossible de calibrer l\'appareil');
    } catch (error) {
      console.error('Erreur lors de la calibration:', error);
      throw error;
    }
  }

  /**
   * Obtenir le texte d'état lisible
   * @param {number} status - Code de statut
   */
  getStatusText(status) {
    const statusMap = {
      0: 'Hors ligne',
      1: 'Occupé',
      2: 'Mise à jour',
      3: 'Erreur',
      4: 'Mise à jour requise',
      5: 'Prêt'
    };
    return statusMap[status] || 'Inconnu';
  }

  /**
   * Obtenir la couleur du statut
   * @param {number} status - Code de statut
   */
  getStatusColor(status) {
    const colorMap = {
      0: 'default',
      1: 'processing',
      2: 'warning',
      3: 'error',
      4: 'error',
      5: 'success'
    };
    return colorMap[status] || 'default';
  }

  /**
   * Obtenir le nom du facteur de test
   * @param {number} factorCode - Code du facteur
   */
  getFactorName(factorCode) {
    const factorMap = {
      1: 'Lumière absorbée',
      2: 'Chlore libre',
      3: 'Chlore total',
      4: 'Chlore combiné',
      5: 'Brome',
      6: 'Biguanide',
      7: 'Biguanide Shock',
      8: 'pH',
      9: 'Alcalinité / Dureté carbonatée',
      10: 'Dureté totale',
      11: 'Dureté calcique',
      12: 'Acide cyanurique',
      13: 'Fer total',
      14: 'Fer ferrique',
      15: 'Fer ferreux',
      16: 'Cuivre',
      17: 'Borate',
      18: 'Phosphate',
      19: 'Sel',
      20: 'Nitrate',
      21: 'Nitrite',
      22: 'Urée',
      23: 'Turbidité',
      24: 'Aluminium',
      25: 'Baryum',
      26: 'Benzotriazole',
      27: 'Bore',
      28: 'Cadmium',
      29: 'Carbohydrazide',
      30: 'Chlorinediox',
      31: 'Chlorure',
      32: 'Chrome',
      33: 'Cobalt',
      34: 'COD',
      35: 'Couleur',
      36: 'Cyanure',
      37: 'DEHA',
      38: 'Oxygène',
      39: 'Erythorbicacid',
      40: 'Fluorure',
      41: 'Hydrazine',
      42: 'Peroxyde',
      43: 'Hydroquinone',
      44: 'Iode',
      45: 'Plomb',
      46: 'Manganèse',
      47: 'Mercure',
      48: 'Ketoxime',
      49: 'Molybdène',
      50: 'Nickel',
      51: 'Oxydant',
      52: 'Ozone',
      53: 'Phénol',
      54: 'Ammoniac',
      55: 'Solides dissous totaux',
      56: 'Potassium',
      57: 'Silice',
      58: 'Tensioactifs',
      59: 'Sulfure',
      60: 'Sulfate',
      61: 'Tanin',
      62: 'Indice de saturation',
      63: 'Température',
      64: 'Alcalinité ajustée',
      65: 'Phosphore',
      66: 'Zinc',
      67: 'Magnésium',
      68: 'CO2',
      69: 'Ions magnésium',
      70: 'Ions calcium'
    };
    return factorMap[factorCode] || `Facteur ${factorCode}`;
  }
}

export default new WaterLinkService();
